import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ticketService } from '../services';
import gatheringService from '../services/gatheringService';
import { useAuth } from '../context';
import { LoadingSpinner } from '../components';

/**
 * TicketClaimPage — the other end of a transfer link.
 *
 * Somebody handed you their seat, probably after you paid them somewhere Party
 * Resume never sees. This page is the whole exchange: it shows what's on offer,
 * and one button takes it.
 *
 * Two things it deliberately does NOT show: the ticket code and the QR. You
 * don't hold the seat until you claim it, and the link is worth exactly one
 * claim — printing the QR here would hand the seat to everyone who saw the URL.
 *
 * No Navbar. This is a page a stranger lands on cold from a Marketplace listing
 * or a text message, and the only two things worth doing on it are claiming and
 * looking at the party.
 */
/**
 * `kind="gathering"` (Oct 2026): the same page for a native gathering seat — the preview comes
 * from the gathering transfer endpoints, the claim lands on `/gt/{code}` and the dead-link
 * button opens the gathering. One page, one prop; never a fork.
 */
export default function TicketClaimPage({ kind = 'event' }) {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const gathering = kind === 'gathering';
  const claimPath = gathering ? `/gatherings/tickets/claim/${token}` : `/ticket/claim/${token}`;

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      if (gathering) {
        const r = await gatheringService.previewClaim(token);
        if (!r.success) throw new Error(r.error);
        setPreview(r.data);
      } else {
        const res = await ticketService.getTransferPreview(token);
        setPreview(res.data);
      }
    } catch {
      setPreview({ state: 'not_found' });
    } finally {
      setLoading(false);
    }
  }, [token, gathering]);

  // Re-read once auth resolves: the same link says "this is your own offer" to
  // the sender and "claim it" to everyone else, and which one you are isn't
  // known until the token has been read off storage.
  useEffect(() => {
    if (!authLoading) load();
  }, [authLoading, load]);

  const handleClaim = async () => {
    setError('');
    setClaiming(true);
    try {
      if (gathering) {
        const r = await gatheringService.claim(token);
        if (!r.success) throw new Error(r.error?.message || r.error || 'Could not claim this seat.');
        navigate(`/gt/${r.data.ticketCode}`, { replace: true });
        return;
      }
      const res = await ticketService.claimTransfer(token);
      // Straight to the seat — it's theirs now, and the QR is the point.
      navigate(res.data.gatheringId ? `/gt/${res.data.ticketCode}` : `/ticket/${res.data.ticketCode}`, { replace: true });
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Could not claim this ticket.');
      setClaiming(false);
      load();
    }
  };

  if (loading || authLoading) return <LoadingSpinner fullScreen />;

  const state = preview?.state || 'not_found';

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="mx-auto max-w-lg px-6 py-10">
        <Link to="/featured" className="font-mono text-[10px] uppercase tracking-widest text-gray-500 hover:text-white">
          ← Portal
        </Link>

        {state === 'claimable' || state === 'own_ticket' ? (
          <Offer
            preview={preview}
            state={state}
            isAuthenticated={isAuthenticated}
            claiming={claiming}
            error={error}
            onClaim={handleClaim}
            token={token}
            claimPath={claimPath}
          />
        ) : (
          <DeadLink state={state} preview={preview} />
        )}
      </div>
    </div>
  );
}

function Offer({ preview, state, isAuthenticated, claiming, error, onClaim, token, claimPath }) {
  const mine = state === 'own_ticket';

  return (
    <div className="mt-8 border-2 border-white">
      <div className="bg-dreamPink px-5 py-4 text-black">
        <p className="font-mono text-xs font-bold uppercase tracking-[0.1em]">
          {mine ? 'This is your offer' : `${preview.senderName} is sending you a ticket`}
        </p>
        {preview.totalInBatch > 1 && (
          <p className="mt-1 font-mono text-[10px] font-bold uppercase tracking-[0.1em]">
            Ticket {preview.ticketNumber} of {preview.totalInBatch}
          </p>
        )}
      </div>

      {preview.eventPosterUrl && (
        <img
          src={preview.eventPosterUrl}
          alt=""
          className="max-h-64 w-full border-b-2 border-white object-cover"
        />
      )}

      <div className="space-y-4 px-5 py-6">
        <h1 className="font-mono text-2xl font-bold leading-tight">{preview.eventTitle}</h1>
        <div className="space-y-1 font-mono text-sm">
          <p className="uppercase tracking-wider">{formatWhen(preview.eventDateTime, preview.eventLocationLongitude)}</p>
          {preview.eventLocationCity && (
            <p className="uppercase tracking-wider text-gray-500">{preview.eventLocationCity}</p>
          )}
          {preview.hostDisplayName && (
            <p className="pt-2 text-xs text-gray-500">Hosted by {preview.hostDisplayName}</p>
          )}
        </div>

        {error && <p className="font-mono text-xs text-red-400">{error}</p>}

        {mine ? (
          <div className="space-y-3 pt-2">
            <p className="font-mono text-[11px] leading-relaxed text-gray-500">
              This is the link you're handing out. Whoever opens it first takes the seat, and
              your copy stops working the moment they do.
            </p>
            <Link
              to="/my-tickets"
              className="block border border-white py-3 text-center font-mono text-xs font-bold uppercase tracking-widest hover:bg-white hover:text-black"
            >
              Back to my tickets
            </Link>
          </div>
        ) : isAuthenticated ? (
          <div className="space-y-3 pt-2">
            <button
              type="button"
              onClick={onClaim}
              disabled={claiming}
              className="w-full bg-white py-4 font-mono text-sm font-bold uppercase tracking-widest text-black transition-opacity hover:opacity-90 disabled:opacity-40"
            >
              {claiming ? 'Claiming…' : 'Claim this ticket →'}
            </button>
            <p className="text-center font-mono text-[10px] uppercase tracking-widest text-gray-500">
              One seat. First tap takes it.
            </p>
          </div>
        ) : (
          <div className="space-y-3 pt-2">
            {/* The seat has to land in an account, so there's no claiming without
                one. Both links carry the destination so the claim survives the
                round trip through sign-in or sign-up. */}
            <Link
              to={`/login?redirect=${encodeURIComponent(claimPath)}`}
              className="block w-full bg-white py-4 text-center font-mono text-sm font-bold uppercase tracking-widest text-black hover:opacity-90"
            >
              Sign in to claim
            </Link>
            <Link
              to={`/register?redirect=${encodeURIComponent(claimPath)}`}
              className="block w-full border border-white py-3 text-center font-mono text-xs font-bold uppercase tracking-widest hover:bg-white hover:text-black"
            >
              Create an account
            </Link>
            <p className="text-center font-mono text-[10px] uppercase tracking-widest text-gray-500">
              One seat. First tap takes it.
            </p>
          </div>
        )}
      </div>

      <div className="border-t border-white/20 px-5 py-3">
        <p className="font-mono text-[10px] leading-relaxed text-gray-600">
          Portal moves the ticket and nothing else. Any money for this seat was arranged
          between the two of you — we don't hold it, take a cut, or guarantee it.
        </p>
      </div>
    </div>
  );
}

/**
 * Claimed, withdrawn, expired or never real. Each is a different sentence, and
 * saying which one it was is the difference between "you were too slow" and
 * "you were sent a bad link".
 */
function DeadLink({ state, preview }) {
  const copy = {
    claimed: ['Already claimed', 'Somebody else got to this one first. The seat is theirs.'],
    canceled: ['Offer withdrawn', 'Whoever sent this took it back. Ask them for a new link.'],
    expired: ['Offer expired', "This link isn't good any more — offers close when the doors open."],
    not_found: ['Link not found', "We can't find an offer at this address. Check the link you were sent."],
  }[state] || ['Link not found', "We can't find an offer at this address."];

  return (
    <div className="mt-8 border-2 border-white p-6 text-center">
      <p className="font-mono text-lg font-bold uppercase tracking-wider">{copy[0]}</p>
      <p className="mx-auto mt-3 max-w-xs font-mono text-xs leading-relaxed text-gray-500">{copy[1]}</p>

      {(preview?.eventInviteCode || preview?.gatheringId) && (
        <Link
          to={preview.gatheringId ? `/gatherings/${preview.gatheringId}` : `/events/${preview.eventInviteCode}`}
          className="mt-6 inline-block border border-white px-6 py-3 font-mono text-xs font-bold uppercase tracking-widest hover:bg-white hover:text-black"
        >
          {preview.gatheringId ? 'See the gathering' : 'See the event'}
        </Link>
      )}
    </div>
  );
}

/** The event's own local clock, the same rule the share card prints. */
function formatWhen(dateStr, longitude) {
  const tz = timeZoneFor(longitude);
  try {
    const d = new Date(dateStr);
    const day = d.toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: tz,
    });
    const midnight = d.toLocaleTimeString('en-US', {
      hour12: false, hour: '2-digit', minute: '2-digit', timeZone: tz,
    }) === '00:00';
    if (midnight) return day;
    return `${day} · ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz })}`;
  } catch {
    return '';
  }
}

function timeZoneFor(lng) {
  if (lng == null) return 'America/Vancouver';
  if (lng > -55) return 'America/St_Johns';
  if (lng > -65) return 'America/Halifax';
  if (lng > -80) return 'America/Toronto';
  if (lng > -100) return 'America/Chicago';
  if (lng > -115) return 'America/Denver';
  return 'America/Vancouver';
}
